
import { Options, Vue } from "vue-class-component";
import LabelAndValue from "@/components/general/LabelAndValue.vue";
import OnlyValue from "@/components/general/OnlyValue.vue";
import { useGeneralInfoStore } from "@/components/task/generalInfo/taskGeneralInfoStore";
import { globalConfig, computeLldAxaGlobalEncours, getSelectedProposalItem, isEncoursAxaGlobal, isLldFinancingProduct, getPartyRoleFromOffer, resolveOfferForLld } from "@/utils";
import { ContactType, Person } from "@/types";
import {
  contacts,
  getPartyClass,
  populateContactsMap,
} from "@/components/summary-panel/summary";
import {
  getYearsOfDate,
  formatDateToMoment,
  formatDate,
  formatedDate,
  formatAmoutswitchCurrency
} from "@/utils/configuration/formatters-config";
import store from "@/store";
import i18n from "@/i18n";
const generalInfoStore = useGeneralInfoStore();

@Options({
  methods: { getYearsOfDate, getPartyClass, formatDateToMoment },
  computed: {
    contacts() {
      return contacts;
    },
    ContactType() {
      return ContactType;
    },
  },
  components: { LabelAndValue, OnlyValue },
  props: ["person", "order", "partyBusinessData", "roleCode", "third", "hideClassification"],
  created() {
    populateContactsMap(this.person?.contacts);
    store.dispatch("middleOfficeModule/searchClassification", {
      filter: {
        id: this.person.resourceUid,
      },
      callback: this.updateResultRows,
    });
  },
})
export default class SummaryPerson extends Vue {
  person?: Person;
  order?: string;
  partyBusinessData?: Record<string, unknown>;
  roleCode?: string;
  hideClassification?: boolean;
  third?: { facilities?: { name?: string; siret?: string }[] };
  rows: any[] = [];
  capitalize = globalConfig.capitalize;
  formatDate = formatDate;
  formatedDate = formatedDate

  valueStyle = {
    "font-weight": "bold",
    "margin-left": "20px",
    color: "black",
  };
  valueFinancialStyle = {
    "font-weight": "bold",
    "margin-left": "5px",
    color: "black",
  };
  divValueStyle = {
    display: "flex",
    "justify-content": "end",
    "padding-right": "10px",
  };
  labelStyleItem = {
    "font-size": "1rem",
    color: "gray",
    "font-weight": "bold",
  };

  get assignee() {
    const task = store.state.taskModule.selectedTask;
    const rawAssignee = task?.assignee || store.state.middleOfficeModule.historiesStep?.[0]?.assignee;
    if (!rawAssignee) {
      return "N/A";
    }
    return rawAssignee.includes(" ## ")
      ? rawAssignee.substring(0, rawAssignee.indexOf(" ## ")).trim()
      : rawAssignee.trim();
  }

  get primaryJob() {
    return this.person?.jobs?.find((el) => !!el);
  }

  get isManagerRole() {
    return this.roleCode === "SG";
  }

  get professionalSituationLabel() {
    const value = this.primaryJob?.positionType?.type?.value;
    return value ? i18n.global.t(`demand.party.positionType.${value}`) : "-";
  }

  get primaryPartyAddress() {
    return this.person?.addresses?.find((el) => !!el);
  }

  translateContractType(value: string, apiLabel?: string) {
    const key = `middleOffice.party.person.occupation.occupationTypes.${value}`;
    if (i18n.global.te(key)) {
      return i18n.global.t(key);
    }
    const baseCode = value.split("_")[0];
    if (baseCode && baseCode !== value) {
      const baseKey = `middleOffice.party.person.occupation.occupationTypes.${baseCode}`;
      if (i18n.global.te(baseKey)) {
        return i18n.global.t(baseKey);
      }
    }
    if (apiLabel && apiLabel !== value) {
      return apiLabel;
    }
    return value;
  }

  get contractTypeLabel() {
    const contractType = this.primaryJob?.contractType?.type;
    const value = contractType?.value;
    if (!value) {
      return "-";
    }
    return this.translateContractType(String(value), contractType?.label);
  }

  get employerSeniority() {
    const years = this.primaryJob?.nbYearExpereinceActivity;
    if (years === undefined || years === null) {
      return "-";
    }
    return String(years);
  }

  get addressStatusLabel() {
    const livingType = this.primaryPartyAddress?.livingSituation?.type;
    const rawValue = livingType?.value;
    if (rawValue === undefined || rawValue === null || rawValue === "") {
      return "-";
    }
    const code = Array.isArray(rawValue) ? String(rawValue[0]) : String(rawValue);
    const key = `middleOffice.party.person.address.livingstatus.${code}`;
    if (i18n.global.te(key)) {
      return i18n.global.t(key);
    }
    if (livingType?.label && livingType.label !== code) {
      return livingType.label;
    }
    return code;
  }

  normalizeToStringArray(raw: unknown): string[] {
    if (raw === undefined || raw === null || raw === "") {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw.map((item) => String(item)).filter(Boolean);
    }
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.map((item) => String(item)).filter(Boolean);
          }
        } catch {
          // ignore invalid JSON
        }
      }
      return trimmed ? [trimmed] : [];
    }
    return [String(raw)];
  }

  translateAddressPreference(code: string) {
    const key = `demand.party.addressPreferenceType.${code}`;
    return i18n.global.te(key) ? i18n.global.t(key) : code;
  }

  formatYesNo(value: unknown) {
    if (value === undefined || value === null || value === "") {
      return "-";
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") {
        return "-";
      }
      const lower = trimmed.toLowerCase();
      if (lower === "true" || trimmed === "1") {
        return i18n.global.t("main.yes");
      }
      if (lower === "false" || trimmed === "0") {
        return i18n.global.t("main.no");
      }
    }
    const normalized = typeof value === "string" ? parseInt(value, 10) : value;
    if (normalized === 1 || normalized === true) {
      return i18n.global.t("main.yes");
    }
    if (normalized === 0 || normalized === false) {
      return i18n.global.t("main.no");
    }
    return String(value);
  }

  get renewalClientLabel() {
    return this.formatYesNo(this.resolvedPartyBusinessData?.renewal);
  }

  get cfClientLabel() {
    return this.formatYesNo(this.resolvedPartyBusinessData?.isCfClient);
  }

  get resolvedPartyBusinessData(): Record<string, unknown> {
    const third = this.third as { businessData?: Record<string, unknown> } | undefined;
    return {
      ...(third?.businessData ?? {}),
      ...(this.person?.businessData ?? {}),
      ...(this.partyBusinessData ?? {}),
    };
  }

  get childrenCount() {
    const dependent = this.person?.nbOfDependent;
    if (dependent !== undefined && dependent !== null) {
      return String(dependent);
    }
    const nbOfChildren = this.person?.nbOfChildren;
    if (nbOfChildren !== undefined && nbOfChildren !== null) {
      return String(nbOfChildren);
    }
    return "-";
  }

  get managedCompanyName() {
    const facilityName = this.third?.facilities?.find((f) => f?.name)?.name;
    if (facilityName) {
      return facilityName;
    }
    return this.primaryJob?.employerName || "-";
  }
  
  get offer(){
    return resolveOfferForLld();
  }
  get offerReference(){
  return this.offer?.reference
  }
  adressType = (addresses: any[]) => {
    const raw = addresses.find((el) => !!el)?.address?.type?.type?.value;
    const codes = this.normalizeToStringArray(raw);
    if (!codes.length) {
      return "";
    }
    return codes.map((code) => this.translateAddressPreference(code)).join(", ");
  };

adressStreetAndStreetNumber = (addresses: any[]) => {
  const address = addresses.find((el) => !!el)?.address;

  if (!address) {
    return "";
  }

  if (address.street && address.streetNum) {
    return `${address.street} ${address.streetNum}`;
  } else if (address.street) {
    return `${address.street}`;
  } else if (address.streetNum) {
    return `${address.streetNum}`;
  }
  else if (address.line1) {
    return `${address.line1}`;
  }
  
  return "";
};
  get columns() {
    return globalConfig.table_config.FinancialexposureCols;
  }
  get financialExposureTable() {
  const rawData = this.person?.financialExposures as unknown as {
    financialInstitution: { value: string, label: string };
    outstandingType: { value: string, label: string };
    netOutstanding: number;
    grossOutstanding: number;
    residualValue: number;
    contractValidity: { until?: string, from?: string };
    contractReference: string;
  }[];

  if (!rawData || rawData.length === 0) {
    return [];
  }
  const offer = resolveOfferForLld();
  const proposalItem = getSelectedProposalItem(offer);
  const isLLD = isLldFinancingProduct(proposalItem?.financingProduct);
  const partyRole = getPartyRoleFromOffer(offer, this.person?.resourceUid);
  const contractReference = offer?.reference;

  const currentDate = new Date(); // Get the current system date

  const groupedData = rawData.reduce((acc: any, el: any) => {
    const key = `${el.financialInstitution.value}_${el.contractReference}`;
    const offerReferencePart = key.split('_').pop();
    
    let isValid = true;
    if (el.contractValidity && el.contractValidity.until && el.contractValidity.until.trim() !== '') {
      const contractUntilDateParts = el.contractValidity.until.split('/');
      if (contractUntilDateParts.length === 3 && 
          contractUntilDateParts[2] && 
          contractUntilDateParts[1] && 
          contractUntilDateParts[0]) {
        const contractUntilDate = new Date(
          Number(contractUntilDateParts[2]), // Year
          Number(contractUntilDateParts[1]) - 1, // Month (subtract 1 because months are zero-based)
          Number(contractUntilDateParts[0]) // Day
        );
        // Check if the contract is still valid (until date is greater than current system date)
        isValid = contractUntilDate > currentDate;
      }
    }
    
    if (isValid && offerReferencePart == this.offerReference) {
      if (!acc[key]) {
        acc[key] = {
          financialInstitution: el.financialInstitution.value,
          contractReference: el.contractReference, // Keep this for reference
          outstandingTypes: {}
        };
      }
      const outstandingTypeKey = el.outstandingType.value;
      if (!acc[key].outstandingTypes[outstandingTypeKey]) {
        acc[key].outstandingTypes[outstandingTypeKey] = {
          netOutstanding: 0,
          grossOutstanding: 0,
          residualValue: 0
        };
      }
      acc[key].outstandingTypes[outstandingTypeKey].netOutstanding = el.netOutstanding || 0;
      if (isLLD && isEncoursAxaGlobal(el)) {
        acc[key].outstandingTypes[outstandingTypeKey].grossOutstanding = computeLldAxaGlobalEncours(
          el,
          rawData,
          proposalItem,
          offer,
          partyRole,
          contractReference
        );
      } else {
        acc[key].outstandingTypes[outstandingTypeKey].grossOutstanding = el.grossOutstanding || 0;
      }
    }
    
    
    return acc;
  }, {});

  // Transforming data for display
  const transformedData = Object.values(groupedData).map((item: any) => ({
    financialInstitution:  `${item.financialInstitution} (${item.contractReference})`,
    outstandingTypes: Object.entries(item.outstandingTypes).map(([key, value]: [string, any]) => ({
      outstandingType: i18n.global.t(
        "middleOffice.party.organization.typeOutstanding." + key),
      netOutstanding: formatAmoutswitchCurrency('EUR', value.netOutstanding),
      grossOutstanding: formatAmoutswitchCurrency('EUR', value.grossOutstanding),
      residualValue: formatAmoutswitchCurrency('EUR', value.residualValue)
    }))
  }));
  return transformedData.filter((item: any) => !item.financialInstitution?.startsWith('AXA'));
}
  adressPostalCodeAndCity = (addresses: any[]) => {
    const address = addresses.find((el) => !!el)?.address;

    if (!address) {
      return "";
    } else if (address.postalCode.postalCode && address.postalCode.city) {
      return `${address.postalCode.postalCode} ${address.postalCode.city}`;
    } else if (address.postalCode.postalCode) {
      return `${address.postalCode.postalCode}`;
    } else if (address.postalCode.city) {
      return `${address.postalCode.city}`;
    }
  };

  updateResultRows(rows: any) {
    this.rows = this.filterLatestValidity(rows);
  }
  filterLatestValidity(rows: any[]) {
    const latestValidityMap = new Map();
    rows.forEach(row => {
      const classTypeCode = row.class_type_code;
      if (!latestValidityMap.has(classTypeCode) ||
        row.validity_from > latestValidityMap.get(classTypeCode).validity_from ||
        (row.validity_from === latestValidityMap.get(classTypeCode).validity_from &&
         row.validity_until > latestValidityMap.get(classTypeCode).validity_until)) {
      latestValidityMap.set(classTypeCode, row);
    }
    });
    const filteredRows = Array.from(latestValidityMap.values());
    return filteredRows;
  }
}
